import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CreateCommunicationRequest,
  GetCommunicationCorrespondenceListResponse,
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
  GetCorrespondenceListV1SrCorrespondencesGetParams,
  GetCorrespondencesResponse,
} from 'api';

export const ISSUER_COMMUNICATIONS_LIST_QUERY_KEY = 'vestra/issuer-communications-class-list';

export const useIssuerCommunicationsListQuery = (
  payload: GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();

  return useInfiniteQuery<GetCommunicationCorrespondenceListResponse>({
    queryKey: [
      ISSUER_COMMUNICATIONS_LIST_QUERY_KEY,
      JSON.stringify(payload),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    retry: 2,
    refetchInterval: 1000 * 60, //1 min
    queryFn: async ({ pageParam = 0 }) => {
      const response =
        await api.v1.getCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGet({
          ...payload,
          page: pageParam || payload.page,
        });
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerCommunicationMessagesQuery = (
  payload: GetCorrespondenceListV1SrCorrespondencesGetParams,
  disabled = false,
) => {
  return useInfiniteQuery<GetCorrespondencesResponse>({
    queryKey: [ISSUER_COMMUNICATIONS_LIST_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    retry: 2,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getCorrespondenceListV1SrCorrespondencesGet({
        ...payload,
        page: pageParam || payload.page,
      });
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerAddAnnouncementMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateCommunicationRequest) => {
      const response = await withErrorHandling(
        api.v1.createCommunicationV1SrCommunicationsPost(payload),
        {
          loading: 'Creating announcement...',
          success: 'Announcement created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ISSUER_COMMUNICATIONS_LIST_QUERY_KEY]);
    },
  });
};
